import React, {useEffect, useMemo, useState} from "react";
import {useParams, useNavigate, useSearchParams} from "react-router-dom";
import {supabase} from "./utils/supabaseClient";
import { useAuth } from "./providers/AuthProvider";
import {Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Center, Heading, Text, VStack, HStack, Icon, Spinner, Container, Tabs, TabList, TabPanels, Tab, TabPanel, Badge, Flex, CloseButton} from "@chakra-ui/react";
import { FiArrowLeft, FiBell, FiBookOpen, FiClipboard, FiInfo, FiStar } from "react-icons/fi";
import {saveAs} from "file-saver";
import {motion} from "framer-motion";
import moment from "moment";
import { useMediaQuery, useToast } from "@chakra-ui/react";
import { getParticipantFullName } from "./utils/formatHelpers";
import { generateICSContent, generateMultipleICSContent, downloadICS } from "./utils/calendarHelpers";
import { CourseOverviewTab } from "./components/CourseOverviewTab";
import { CourseResourcesTab } from "./components/CourseResourcesTab";
import { AssessmentResourcesTab } from "./components/AssessmentResourcesTab";
import { CourseEvaluationTab } from "./components/CourseEvaluationTab";
import { NoticesTab } from "./components/NoticesTab";
import { COLORS } from "./styles/colors";
import {
    fetchEvaluationResponse,
    fetchPublishedEvaluationForm,
    isEvaluationOpenForCourseRun,
    resolveRegistrationForCourseRun,
} from "./utils/lmsEvaluations";
import { getStoredViewAsStudent } from "./utils/staffHelpers";

const TAB_KEYS = ["overview", "materials", "assessments", "evaluation", "notices"];

export const EachCourse = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const [courseMaterials, setCourseMaterials] = useState(null);
    const [courseResources, setCourseResources] = useState([]);
    const {courseRunId} = useParams();
    const [isMobile] = useMediaQuery("(max-width: 768px)");
    const toast = useToast();
    const [registration, setRegistration] = useState(null);
    const [evaluationDue, setEvaluationDue] = useState(false);
    const [bannerDismissed, setBannerDismissed] = useState(false);
    const [evalRefreshKey, setEvalRefreshKey] = useState(0);

    const tabIndex = useMemo(() => {
        const key = (searchParams.get("tab") || "overview").toLowerCase();
        const idx = TAB_KEYS.indexOf(key);
        return idx >= 0 ? idx : 0;
    }, [searchParams]);

    const setTabIndex = (index) => {
        const key = TAB_KEYS[index] || "overview";
        const next = new URLSearchParams(searchParams);
        if (key === "overview") next.delete("tab");
        else next.set("tab", key);
        setSearchParams(next, { replace: true });
    };

    const handleDownloadSessionCalendar = (schedule, moduleName, allSessions) => {
        const dateStr = moment(schedule.date).format("YYYY-MM-DD");
        const sessionForICS = {
            date: dateStr,
            start_time: schedule.start_time,
            end_time: schedule.end_time,
            venue: schedule.venue,
            venueAddress: schedule.venueAddress,
            room: schedule.room,
            session_id: schedule.session_id || schedule.date,
            meeting_url: schedule.meeting_url || "",
            is_online: !!schedule.is_online || !!schedule.meeting_url,
        };
        let sessionNumber = null, totalSessions = null;
        if (allSessions && Array.isArray(allSessions)) {
            const sorted = [...allSessions].sort((a, b) => moment(a.date).diff(moment(b.date)));
            const idx = sorted.findIndex(s => moment(s.date).format("YYYY-MM-DD") === dateStr && s.start_time === schedule.start_time);
            if (idx !== -1) { sessionNumber = idx + 1; totalSessions = sorted.length; }
        }
        downloadICS(generateICSContent(sessionForICS, moduleName, sessionNumber, totalSessions), `${moduleName.replace(/\s+/g, "_")}_${moment(schedule.date).format("YYYYMMDD")}.ics`);
        if (!isMobile) { toast({ title: "Calendar Event Downloaded", description: "Open the .ics file to add to your calendar.", status: "success", duration: 3000, isClosable: true }); }
    };

    const handleDownloadAllSessionsCalendar = () => {
        if (!courseMaterials?.course_dates || courseMaterials.course_dates.length === 0) {
            toast({ title: "No Sessions", description: "No scheduled sessions yet.", status: "warning", duration: 3000, isClosable: true }); return;
        }
        const moduleName = courseMaterials?.course_type?.name || "Module";
        const sessions = courseMaterials.course_dates.map(cd => ({
            date: moment(cd.date).format("YYYY-MM-DD"),
            start_time: cd.start_time,
            end_time: cd.end_time,
            venue: cd.venue,
            venueAddress: cd.venueAddress,
            room: cd.room,
            session_id: cd.date,
            meeting_url: cd.meeting_url || "",
            is_online: !!cd.is_online || !!cd.meeting_url,
        }));
        downloadICS(generateMultipleICSContent(sessions, moduleName), `${moduleName.replace(/\s+/g, "_")}_All_Sessions.ics`);
        if (!isMobile) { toast({ title: "Calendar Events Downloaded", description: `${sessions.length} sessions downloaded.`, status: "success", duration: 4000, isClosable: true }); }
    };

    // Prefilled enquiry links
    const courseNameForEnquiry = courseMaterials?.course_type?.name || "this course";
    const courseRunCodeForEnquiry = courseMaterials?.id || "\u2014";
    const courseRunNameForEnquiry = courseMaterials?.course_type?.name || courseNameForEnquiry;
    const getEnquiryMessage = () => {
        const participantName = getParticipantFullName(user);
        return `Hello, I am ${participantName} currently enrolled in ${courseNameForEnquiry}, I have question(s) about ${courseRunNameForEnquiry} (${courseRunCodeForEnquiry}):\n\nMy questions is/are:\n1) `;
    };
    const getMailtoEnquiryUrl = (email) => {
        if (!email) return null;
        const subject = encodeURIComponent(`Enquiry: ${courseRunNameForEnquiry} (${courseRunCodeForEnquiry})`);
        const body = encodeURIComponent(getEnquiryMessage());
        return `mailto:${email}?subject=${subject}&body=${body}`;
    };
    const getWhatsAppEnquiryUrl = (phone) => {
        if (!phone || typeof phone !== "string") return null;
        const digits = phone.replace(/\D/g, "");
        const normalized = digits.length === 8 && /^[89]/.test(digits) ? `65${digits}` : digits.startsWith("65") ? digits : digits.length >= 8 ? `65${digits.slice(-8)}` : digits;
        if (normalized.length < 8) return null;
        const text = encodeURIComponent(getEnquiryMessage());
        return `https://wa.me/${normalized}?text=${text}`;
    };

    const getAllCourseMaterials = async () => {
        const {data, error } = await supabase
            .from("course_run")
            .select(`
                id,
                course_type_code,
                start_date,
                end_date,
                venue,
                attachment,
                attendance_req,
                max_capacity,
                is_assessed,
                assessment_workflow_status,
                is_course_run_confirmed,
                instructor_name,
                trainer_id,
                venue_id,
                room_id,
                trainers (
                    name,
                    email
                ),
                venues (
                    name,
                    address,
                    postal_code,
                    phone,
                    contact_person
                ),
                rooms (
                    name,
                    capacity,
                    floor,
                    unit
                ),
                course_type (
                    code,
                    name,
                    short_desc,
                    course_resources,
                    assessment_resources,
                    course_objectives,
                    course_duration,
                    minimum_entry_requirements,
                    prerequisites,
                    price,
                    certification,
                    school
                )
            `)
            .eq("id", courseRunId) 
            .single();

        if (data) {
            const { data: sessions, error: sessionsError } = await supabase
                .from("session")
                .select("*")
                .eq("course_run_code", courseRunId)
                .order("session_date", { ascending: true });

            if (sessionsError) {
                console.error("Error fetching sessions:", sessionsError);
            }

            const roomIds = [...new Set((sessions || []).filter(s => s.room_id && !s.custom_room).map(s => s.room_id))];
            const roomMap = {};
            if (roomIds.length > 0) {
                const { data: rooms } = await supabase
                    .from("rooms")
                    .select("code, name, floor, unit")
                    .in("code", roomIds);
                rooms?.forEach(r => { roomMap[r.code] = r; });
            }

            const venueIds = [...new Set((sessions || []).filter(s => s.venue_id && !s.custom_venue).map(s => s.venue_id))];
            const venueMap = {};
            if (venueIds.length > 0) {
                const { data: venues } = await supabase
                    .from("venues")
                    .select("code, name, address, is_online")
                    .in("code", venueIds);
                venues?.forEach(v => { venueMap[v.code] = v; });
            }

            const course_dates = (sessions || []).map(session => {
                const roomName = session.custom_room || (session.room_id && roomMap[session.room_id]?.name) || session.room_id;
                const venueObj = session.venue_id && venueMap[session.venue_id];
                const venueName = session.custom_venue || (venueObj?.name) || session.venue_id;
                const isOnline = !!venueObj?.is_online || !!session.meeting_url;
                const venueAddress = isOnline ? "" : (venueObj?.address || "");
                return {
                    date: session.session_date,
                    start_time: session.start_time ? moment(session.start_time, "HH:mm:ss").format("HHmm") : null,
                    end_time: session.end_time ? moment(session.end_time, "HH:mm:ss").format("HHmm") : null,
                    room: roomName,
                    venue: venueName,
                    venueAddress: venueAddress,
                    session_id: session.session_id || session.id,
                    meeting_url: session.meeting_url || "",
                    is_online: isOnline,
                };
            });

            setCourseMaterials({
                ...data,
                course_dates: course_dates
            });
        }
        if (error) {
            console.error("Error fetching course materials:", error);
        }
    };

    const fetchCourseResourcesForRun = async (runId) => {
        const today = moment().format("YYYY-MM-DD");
        const { data: activations, error: actError } = await supabase
            .from("course_run_resource_activations")
            .select("resource_item_id")
            .eq("course_run_id", runId)
            .eq("is_active", true)
            .or(`available_from.is.null,available_from.lte.${today}`)
            .or(`available_until.is.null,available_until.gte.${today}`);

        if (actError || !activations?.length) {
            setCourseResources([]);
            return;
        }

        const resourceIds = activations.map((a) => a.resource_item_id);
        const { data: items, error: itemsError } = await supabase
            .from("course_resource_items")
            .select("id, section_name, section_order, item_name, item_type, item_order")
            .in("id", resourceIds)
            .order("section_order", { ascending: true })
            .order("item_order", { ascending: true });

        if (itemsError || !items?.length) {
            setCourseResources([]);
            return;
        }

        const sectionMap = new Map();
        items.forEach((item) => {
            const key = `${item.section_order}-${item.section_name}`;
            if (!sectionMap.has(key)) {
                sectionMap.set(key, {
                    id: `section-${item.section_order}`,
                    resource_name: item.section_name,
                    items: [],
                });
            }
            sectionMap.get(key).items.push({
                id: item.id,
                type: item.item_type,
                item_name: item.item_name,
            });
        });

        const resources = Array.from(sectionMap.values()).sort(
            (a, b) => parseInt(a.id.replace("section-", ""), 10) - parseInt(b.id.replace("section-", ""), 10)
        );
        setCourseResources(resources);
    };

    const downloadFile = async (filePath) => {
        try {
            const { data, error } = await supabase.storage.from("Course Files").download(filePath);
            if (error) throw error;
            const fileName = filePath.split("/").pop();
            saveAs(data, fileName);
        } catch (error) {
            console.error("Error downloading file:", error);
        }
    };

    useEffect(() => {
        getAllCourseMaterials();
        if (courseRunId) {
            fetchCourseResourcesForRun(courseRunId);
        }
    }, [courseRunId]);

    useEffect(() => {
        let cancelled = false;
        const loadEvalState = async () => {
            if (!courseRunId || !user) {
                setRegistration(null);
                setEvaluationDue(false);
                return;
            }
            // Staff "View as student" is stored in session — use that identity here too
            const preview = getStoredViewAsStudent();
            const targetUserId = preview?.userId || user.id;
            const targetEmail = preview?.email || user.email;

            const reg = await resolveRegistrationForCourseRun({
                userId: targetUserId,
                email: targetEmail,
                courseRunId,
            });
            if (cancelled) return;
            setRegistration(reg);

            const form = await fetchPublishedEvaluationForm();
            if (cancelled) return;
            if (!form || !reg?.id) {
                setEvaluationDue(false);
                return;
            }
            // Need course end_date for relative availability — wait until materials loaded
            const runForAvailability = courseMaterials?.id === courseRunId
                ? courseMaterials
                : { id: courseRunId, end_date: courseMaterials?.end_date };
            if (!isEvaluationOpenForCourseRun(form, runForAvailability)) {
                setEvaluationDue(false);
                return;
            }
            const existing = await fetchEvaluationResponse({
                formId: form.id,
                registrationId: reg.id,
                courseRunId,
            });
            if (cancelled) return;
            setEvaluationDue(!existing);
        };
        loadEvalState();
        return () => {
            cancelled = true;
        };
    }, [courseRunId, user?.id, user?.email, evalRefreshKey, courseMaterials?.id, courseMaterials?.end_date]);

    if (!courseMaterials) {
        return (
            <Center minH="100vh">
                <Spinner size="xl" />
            </Center>
        );
    }
    
    // Parse assessment resources (course resources now come from TMS tables)
    let assessmentResources = [];
    
    try {
        if (courseMaterials?.course_type?.assessment_resources) {
            const parsed = typeof courseMaterials.course_type.assessment_resources === 'string'
                ? JSON.parse(courseMaterials.course_type.assessment_resources)
                : courseMaterials.course_type.assessment_resources;
            assessmentResources = Array.isArray(parsed) ? parsed : [];
        }
    } catch (e) {
        console.error('Error parsing assessment_resources:', e);
    }
    
    const tabProps = {
        flex: 1,
        minW: 0,
        fontSize: { base: "2xs", md: "sm" },
        py: { base: 2.5, md: 3 },
        px: { base: 2, md: 4 },
        borderRadius: "lg",
        border: "2px solid",
        borderColor: "gray.200",
        bg: "white",
        color: "gray.600",
        fontWeight: "500",
        _selected: {
            color: "gray.900",
            fontWeight: "700",
            borderColor: "brand.500",
            bg: "brand.50",
            boxShadow: "sm",
        },
        _hover: { color: "gray.800", bg: "gray.50", borderColor: "gray.300" },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: { base: 0.5, md: 2 },
        minH: { base: "52px", md: "48px" },
    };

    return (
        <Box
            minH="100vh"
            pt={{ base: 2, md: 3 }}
            pb={{ base: 3, md: 5 }}
            bg={COLORS.isabelline}
            backgroundImage={`linear-gradient(180deg, ${COLORS.isabelline} 0%, #EEF1F7 55%, ${COLORS.isabelline} 100%)`}
        >
            <Container maxW="container.2xl" px={{ base: 3, md: 5, xl: 8 }}>
                <Button
                    variant="outline"
                    mb={{ base: 3, md: 4 }}
                    borderRadius="full"
                    leftIcon={<Icon as={FiArrowLeft} boxSize={5} />}
                    onClick={() => navigate("/enrolled-courses")}
                    bg="white"
                    borderColor="gray.200"
                    color={COLORS.maastrichtBlue}
                    fontWeight="600"
                    _hover={{ bg: "gray.50", borderColor: "gray.300" }}
                >
                    Back To Courses
                </Button>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Box
                        bg="white"
                        p={{ base: 4, md: 6, xl: 8 }}
                        borderRadius="2xl"
                        boxShadow="sm"
                        border="1px solid"
                        borderColor="gray.200"
                    >
                        <VStack align="stretch" spacing={3} mb={6}>
                            <Flex justify="space-between" align="start" flexWrap="wrap" gap={2}>
                                <Box flex="1" minW={{ base: 0, md: "300px" }}>
                                    <HStack mb={2} flexWrap="wrap" spacing={2}>
                                        <Badge
                                            colorScheme="teal"
                                            fontSize="xs"
                                            px={3}
                                            py={1}
                                            borderRadius="full"
                                            textTransform="none"
                                        >
                                            {courseMaterials?.id || courseMaterials?.course_type?.code}
                                        </Badge>
                                        {courseMaterials?.is_course_run_confirmed && (
                                            <Badge
                                                colorScheme="green"
                                                fontSize="xs"
                                                px={3}
                                                py={1}
                                                borderRadius="full"
                                                textTransform="none"
                                            >
                                                Confirmed
                                            </Badge>
                                        )}
                                        {courseMaterials?.is_assessed && (
                                            <Badge
                                                colorScheme="orange"
                                                fontSize="xs"
                                                px={3}
                                                py={1}
                                                borderRadius="full"
                                                textTransform="none"
                                            >
                                                Assessed
                                            </Badge>
                                        )}
                                    </HStack>
                                    <Heading
                                        mb={2}
                                        color={COLORS.maastrichtBlue}
                                        lineHeight="shorter"
                                        fontSize={{ base: "xl", md: "2xl" }}
                                        fontWeight="700"
                                        letterSpacing="-0.02em"
                                    >
                                        {courseMaterials?.course_type?.name || "Course"}
                                    </Heading>
                                    {courseMaterials?.start_date && courseMaterials?.end_date && (
                                        <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" fontWeight="500">
                                            {moment(courseMaterials.start_date).format("DD MMM YYYY")} – {moment(courseMaterials.end_date).format("DD MMM YYYY")}
                                        </Text>
                                    )}
                                </Box>
                            </Flex>
                        </VStack>

                        {evaluationDue && !bannerDismissed && TAB_KEYS[tabIndex] !== "evaluation" ? (
                            <Alert
                                status="warning"
                                variant="subtle"
                                borderRadius="xl"
                                mb={5}
                                alignItems="flex-start"
                            >
                                <AlertIcon mt={1} />
                                <Box flex="1">
                                    <AlertTitle fontSize="sm">Evaluation due</AlertTitle>
                                    <AlertDescription fontSize="sm" display="block">
                                        Please complete the course evaluation for this module.
                                    </AlertDescription>
                                    <Button
                                        mt={3}
                                        size="sm"
                                        colorScheme="orange"
                                        onClick={() => setTabIndex(TAB_KEYS.indexOf("evaluation"))}
                                    >
                                        Fill evaluation
                                    </Button>
                                </Box>
                                <CloseButton
                                    size="sm"
                                    onClick={() => setBannerDismissed(true)}
                                    aria-label="Dismiss evaluation banner"
                                />
                            </Alert>
                        ) : null}

                        <Tabs
                            colorScheme="brand"
                            variant="unstyled"
                            isFitted
                            isLazy
                            index={tabIndex}
                            onChange={setTabIndex}
                        >
                            <TabList
                                mb={6}
                                display="flex"
                                flexWrap="nowrap"
                                gap={{ base: 1.5, md: 3 }}
                                bg="gray.50"
                                p={2}
                                borderRadius="lg"
                            >
                                <Tab {...tabProps}>
                                    <Icon as={FiInfo} boxSize={5} flexShrink={0} />
                                    <Text as="span" noOfLines={1} textAlign="center" w="100%">Overview</Text>
                                </Tab>
                                <Tab {...tabProps}>
                                    <Icon as={FiBookOpen} boxSize={5} flexShrink={0} />
                                    <Text as="span" noOfLines={1} textAlign="center" w="100%">
                                        <Text as="span" display={{ base: "inline", md: "none" }}>Materials</Text>
                                        <Text as="span" display={{ base: "none", md: "inline" }}>Course Materials</Text>
                                    </Text>
                                </Tab>
                                <Tab {...tabProps}>
                                    <Icon as={FiClipboard} boxSize={5} flexShrink={0} />
                                    <Text as="span" noOfLines={1} textAlign="center" w="100%">Assessments</Text>
                                </Tab>
                                <Tab {...tabProps}>
                                    <Icon as={FiStar} boxSize={5} flexShrink={0} />
                                    <Text as="span" noOfLines={1} textAlign="center" w="100%">
                                        Evaluation
                                        {evaluationDue ? (
                                            <Badge ml={1} colorScheme="orange" fontSize="2xs" verticalAlign="middle">
                                                Due
                                            </Badge>
                                        ) : null}
                                    </Text>
                                </Tab>
                                <Tab {...tabProps}>
                                    <Icon as={FiBell} boxSize={5} flexShrink={0} />
                                    <Text as="span" noOfLines={1} textAlign="center" w="100%">Notices</Text>
                                </Tab>
                            </TabList>

                            <TabPanels>
                                <TabPanel px={0}>
                                    <CourseOverviewTab
                                        courseMaterials={courseMaterials}
                                        handleDownloadSessionCalendar={handleDownloadSessionCalendar}
                                        handleDownloadAllSessionsCalendar={handleDownloadAllSessionsCalendar}
                                        mailtoUrl={getMailtoEnquiryUrl(courseMaterials?.trainers?.email)}
                                        whatsappUrl={getWhatsAppEnquiryUrl(courseMaterials?.venues?.phone)}
                                    />
                                </TabPanel>
                                <TabPanel px={0}>
                                    <CourseResourcesTab
                                        courseResources={courseResources}
                                        downloadFile={downloadFile}
                                    />
                                </TabPanel>
                                <TabPanel px={0}>
                                    <AssessmentResourcesTab
                                        assessmentResources={assessmentResources}
                                        downloadFile={downloadFile}
                                    />
                                </TabPanel>
                                <TabPanel px={0}>
                                    <CourseEvaluationTab
                                        courseRun={courseMaterials}
                                        courseRunId={courseMaterials?.id || courseRunId}
                                        registration={registration}
                                        onSubmitted={() => {
                                            setEvaluationDue(false);
                                            setBannerDismissed(true);
                                            setEvalRefreshKey((k) => k + 1);
                                        }}
                                    />
                                </TabPanel>
                                <TabPanel px={0}>
                                    <NoticesTab
                                        mode="course"
                                        courseTypeCode={courseMaterials?.course_type_code || courseMaterials?.course_type?.code}
                                        courseRunId={courseMaterials?.id || courseRunId}
                                        showHeader={false}
                                        includeCollege={false}
                                    />
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
};
